import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import throttle from 'lodash/throttle';
import {
  setListVehicleTracking,
  setStatisticVehicleStatus
} from 'src/features/vehicleSlice';
import { useAccessToken } from './useAccessToken';

const useVehicleTracking = () => {
  const dispatch = useDispatch();

  // Authentication & IDs
  // const accessToken = useMemo(() => localStorage.getItem('access-token'), []);

  const accessToken = useAccessToken();

  const { adminId, agencyId } = useSelector(s => s.authSlice);
  const userId = adminId || agencyId;

  // Refs: data queue and vehicle map
  const queueRef = useRef([]);
  const mapRef = useRef({});

  // WebSocket URL
  const wsUrl = useMemo(() => {
    if (!userId || !accessToken) return null;
    return `${process.env.REACT_APP_CDN_WSS_SERVER}/realtime/${userId}?token=${accessToken}`;
  }, [userId, accessToken]);

  // Process queued updates (throttled)
  const processQueue = useCallback(() => {
    const updates = queueRef.current.splice(0);
    let hasChange = false;

    updates.forEach(u => {
      const current = mapRef.current[u.device_id];
      if (!current) return;
      const merged = { ...current, ...u };

      // compare relevant fields
      const changed = [
        'lat',
        'lng',
        'created_at',
        'speed_gps',
        'device_status',
        'direction',
        'engine_status'
      ].some(key => current[key] !== merged[key]);

      if (changed) {
        mapRef.current[u.device_id] = merged;
        hasChange = true;
      }
    });

    if (hasChange) {
      dispatch(setListVehicleTracking(Object.values(mapRef.current)));
    }
  }, [dispatch]);

  const throttledProcess = useMemo(
    () => throttle(processQueue, 500, { leading: true, trailing: true }),
    [processQueue]
  );

  // WebSocket setup
  const { sendJsonMessage, readyState } = useWebSocket(wsUrl, {
    share: false,
    shouldReconnect: () => true,
    onOpen: () => {
      sendJsonMessage({
        event: 'subscribe',
        data: { channel: 'general-chatroom' }
      });
    },
    onMessage: useCallback(
      event => {
        if (!(event.data instanceof Blob)) return;
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const v = JSON.parse(reader.result);
            const { deviceId, serial, data, timeStamp: ts, status } = v;
            if (!deviceId || !serial) return;

            const arr = data['254'];
            const [lat, lng] = Array.isArray(arr)
              ? arr
              : [undefined, undefined];

            queueRef.current.push({
              device_id: deviceId,
              device_serial: serial,
              created_at: ts,
              engine_status: data['259'],
              direction: data['255'],
              speed_gps: data['261'],
              device_status: status,
              ...(lat && lng && { lat, lng })
            });

            throttledProcess();
          } catch (e) {
            console.error('WS parse error', e);
          }
        };
        reader.readAsText(event.data);
      },
      [throttledProcess]
    )
  });

  // Initialize map from Redux list
  const listVehicleTracking = useSelector(
    s => s.vehicleSlice.listVehicleTracking
  );
  useEffect(() => {
    const map = {};
    listVehicleTracking
      .filter(v => v.license_plate)
      .sort((a, b) => a.license_plate.localeCompare(b.license_plate))
      .forEach(v => {
        map[v.device_id] = { ...v };
      });
    mapRef.current = map;
  }, [listVehicleTracking]);

  // Combined stats & lost-GMS check every 5s
  useEffect(() => {
    const handler = () => {
      const now = Date.now();
      const map = mapRef.current;
      let hasMapChange = false;
      const stats = {
        moving: 0,
        stopped: 0,
        lost_gps: 0,
        lost_gms: 0,
        over_speed: 0
      };
      Object.values(mapRef.current).forEach(v => {
        // stats
        if (v.device_status in stats) stats[v.device_status]++;

        // lost_gms logic
        const last = v.created_at * 1000;
        const diff = (now - last) / 1000;
        const shouldLost =
          (v.engine_status === 1 && diff > 20) ||
          (v.engine_status === 0 && diff > 3600);
        if (shouldLost && v.device_status !== 'lost_gms') {
          const device_id = v.device_id;
          map[device_id] = { ...v, device_status: 'lost_gms' };
          hasMapChange = true;
          stats.lost_gms++;
          stats[v.device_status] = Math.max(0, stats[v.device_status] - 1);
        }
      });
      dispatch(setStatisticVehicleStatus(stats));
      if (hasMapChange) {
        dispatch(setListVehicleTracking(Object.values(map)));
      }
    };

    const id = setInterval(handler, 5000);
    return () => clearInterval(id);
  }, [dispatch]);

  return { readyState, sendJsonMessage };
};

export default useVehicleTracking;
